/**
 * Notification settings in the app (2026-10-29): the protocol's rules
 * (packages/protocol notifications.ts, the server's own copy) read against
 * the store. Pure, so the reducer can count mention badges the server's way
 * and the screens can dim muted channels and drop their unread dots.
 *
 * While the Console's `notifications` flag is off, every saved setting
 * counts for nothing here, as on the server: the flag probe (`wire.ts` sets
 * it to the live config) says so; it is on until told otherwise.
 */
import {
  ChannelType,
  channelMuted,
  effectiveLevel,
  guildMuted,
  inheritedLevel,
  isThreadChannelType,
  mentionCounts,
  messageNotifies,
  muteActive,
  overrideOf,
  type Channel,
  type EffectiveNotificationLevel,
  type MentionKind,
  type Message,
  type Snowflake,
  type UserGuildSettings,
} from '@neosocial/protocol';
import type { DataState } from '../store/reducer';

let flagProbe: () => boolean = () => true;

/** Who says whether the `notifications` flag is on (the live config, from wire.ts). */
export function setNotifyFlagProbe(fn: () => boolean): void {
  flagProbe = fn;
}

export function notifyOn(): boolean {
  return flagProbe();
}

type S = Pick<DataState, 'guildSettings' | 'guilds'>;

/** The user's settings in a server, while the flag is on (undefined: the defaults). */
export function settingsIn(
  s: S,
  guildId: Snowflake | null | undefined,
): UserGuildSettings | undefined {
  if (!guildId || !notifyOn()) return undefined;
  return s.guildSettings[guildId];
}

/** The server's default level (Discord's `default_message_notifications`; Only @mentions when unknown). */
export function serverDefault(s: S, guildId: Snowflake | null | undefined): number {
  return (guildId ? s.guilds[guildId]?.default_message_notifications : undefined) ?? 1;
}

const isPrivate = (c: Pick<Channel, 'type'>) =>
  c.type === ChannelType.DM || c.type === ChannelType.GROUP_DM;

/** A server channel or category muted now (itself, its category or the server). DMs: never. */
export function isMuted(s: S, c: Channel | undefined, now = Date.now()): boolean {
  if (!c?.guild_id || isPrivate(c)) return false;
  return channelMuted(settingsIn(s, c.guild_id), c, now);
}

/** The server muted now. */
export function isGuildMuted(
  s: S,
  guildId: Snowflake | null | undefined,
  now = Date.now(),
): boolean {
  return guildMuted(settingsIn(s, guildId), now);
}

/** Muted by its own override (not only through its category or the server). */
export function isOwnMute(s: S, c: Channel, now = Date.now()): boolean {
  const o = overrideOf(settingsIn(s, c.guild_id), c.id);
  return !!o && muteActive(o.muted, o.mute_config, now);
}

/** A category's or channel's level now (its override, else what it inherits). */
export function levelOf(s: S, c: Channel): EffectiveNotificationLevel {
  return effectiveLevel(settingsIn(s, c.guild_id), serverDefault(s, c.guild_id), c);
}

/** What it would inherit with its own override on "default". */
export function inheritedOf(s: S, c: Channel): EffectiveNotificationLevel {
  return inheritedLevel(settingsIn(s, c.guild_id), serverDefault(s, c.guild_id), c);
}

/**
 * Every way a message mentions the signed-in user (by name or a reply to
 * them, a role they hold, @everyone / @here); empty when it does not.
 */
export function mentionKindsOf(
  s: Pick<DataState, 'me' | 'members'>,
  m: Message,
  ch: Channel | undefined,
): MentionKind[] {
  const me = s.me?.id;
  if (!me) return [];
  const out: MentionKind[] = [];
  if (m.mentions.some((u) => u.id === me)) out.push('user');
  const gid = ch?.guild_id;
  const mine = gid ? (s.members[gid]?.[me]?.roles ?? []) : [];
  if (m.mention_roles.some((r) => mine.includes(r))) out.push('role');
  if (m.mention_everyone) out.push('everyone');
  return out;
}

/** Whether a mention raises the badge here (the server's `mentionCounts`). */
export function mentionAllowed(
  s: S,
  ch: Channel | undefined,
  kinds: readonly MentionKind[],
): boolean {
  if (!kinds.length) return false;
  if (!ch?.guild_id) return true;
  return mentionCounts(settingsIn(s, ch.guild_id), serverDefault(s, ch.guild_id), ch, kinds);
}

/**
 * Whether a message notifies (desktop notification and sound): a DM always
 * (the caller has checked ignores and requests); in a server, by the
 * settings (`messageNotifies`: mentions that count, else All messages and
 * not muted).
 */
export function messageNotifiesHere(
  s: Pick<DataState, 'guildSettings' | 'guilds' | 'me' | 'members' | 'channels'>,
  m: Message,
  ch: Channel | undefined,
  now = Date.now(),
): boolean {
  if (!ch) return false;
  if (isPrivate(ch)) return true;
  if (!ch.guild_id) return false;
  let kinds = mentionKindsOf(s, m, ch);
  let at = ch;
  // a thread (threads + notifications): only once joined, unless it names you
  // (which adds you); @everyone and roles ping only its members; its parent
  // channel's settings apply, as on the server
  if (isThreadChannelType(ch.type)) {
    if (!ch.member) {
      if (!kinds.includes('user')) return false;
      kinds = ['user'];
    }
    at = (ch.parent_id ? s.channels[ch.parent_id] : undefined) ?? ch;
  }
  return messageNotifies(settingsIn(s, ch.guild_id), serverDefault(s, ch.guild_id), at, kinds, now);
}
